// pleatedscreenn.js - Pleated Screen configurator
// Rebuilt on the patio door template (patiodoors/pdt/pd.js): same top bar / floating
// navigation, toolbar, save/load, CRM sync and View Details panel. The drawing is
// rendered through DrawWindow -> DrawPleatedScreen (document.type 'pleatedscreens').
//
// doorData stays compatible with the original pleatedscreen.js items so the
// pleated_screen_template / pleated_screen_priceRules keep working:
//   frameData.selectedDoor {id, name}, frameWidth, frameHeight, frameWidthLabel,
//   measurementType, magnetLatch, subFrame, screenFrameColor, gentekColor(_label)
// New keys: frameData.profile ('Z-28' | 'Z-19-45'); items without it load as Z-28.
//           frameData.orientation ('Left to Right' | 'Right to Left', single leaf only, else null)
//           frameData.frameWidth_mm / frameHeight_mm

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { authFetch } from './utils/api';
import { alertC, confirmC } from './components/dialogService';

import { useCrmSettings } from './CRM/SimpleCRM/CrmSettingsContext';
import DrawWindow from './DrawWindow';

import ExpandableSection from './expandablesection';
import './entrydoor.css';
import './toolbar.css';
import './spinner.css';

import { fetchUserField } from './utils/userSettings';

// Pleated Screen Controls
import ScreenProfile, { getScreenProfile } from './controls/screen/ScreenProfile';
import ScreenType, { getScreenType } from './controls/screen/ScreenType';
import MeasurmentType, { SCREEN_SIZE } from './controls/screen/MeasurmentType';
import MagnetLatch from './controls/screen/MagnetLatch';
import ScreenOrientation, { getScreenOrientation } from './controls/screen/ScreenOrientation';
import SizeMm, { inchToMm, mmToInch } from './controls/screen/SizeMm';
import FrameWidthEticomN from './controls/frame/eticom/FrameWidthEticomN';
import FrameHeightEticomN from './controls/frame/eticom/FrameHeightEticomN';
import ExteriorDoor from './controls/finish/ExteriorDoor';
import GentekColor from './controls/finish/GentekColor';

import LocationTag from './controls/LocationTag';
import Notes from './controls/Notes';
import Discount from './controls/frame/Discount';
import Quantity from './controls/frame/Quantity';
import CascadingCombo from './additional/CascadingCombo';
import { toPdfAuto } from './components/toPdf';
import EmailDialog from './records/EmailDialog';

import ViewDetails from './docs/viewdetails';
import { formatToDecimal, formatToFractional } from './controls/conversions';
import { getCookie } from './utils/cookies';
import { get_crm_user_id } from './CRM/SimpleCRM/crmUtils';
import { authFetchRequest } from './utils/api';
import { getCustomerEmail } from './utils/QuickDataAccess';
import { createCrmProduct, updateCrmProduct } from './CRM/ZohoCrm';

// Floating menus - shared from windows
import SmallFloatingNavMenu from './windows/wint/components/SmallFloatingNavMenu';
import LargeFloatingNavMenu from './windows/wint/components/LargeFloatingNavMenu';
import TopBarNavMenu from './windows/wint/components/TopBarNavMenu';
import TopBarToolbarMenu from './windows/wint/components/TopBarToolbarMenu';
import { useThemeColors } from './utils/useThemeColors';
import SmallFloatingToolbarMenu from './windows/wint/components/SmallFloatingToolbarMenu';

const DOCUMENT_TYPE = "pleatedscreens";
const ITEM_PATH = "pleatedscreenn";
const QUOTE_TEMPLATE = "pleated_screen_template";
const PRICE_RULES = "pleated_screen_priceRules";

const DEFAULT_CONFIG = {
  SCREEN_PROFILE: 'Z-28',
  SCREEN_TYPE: 'Single',
  SCREEN_WIDTH: '40"',
  SCREEN_HEIGHT: '80"',
};

const PleatedScreenN = ({ config: configProp = {} }) => {
  const config = { ...DEFAULT_CONFIG, ...configProp };
  const { settings: crmSettings } = useCrmSettings();
  const crmDefined = crmSettings.external_crm !== 'none';
  const tc = useThemeColors();

  // ============================================
  // UI STATE
  // ============================================
  const [showOriginalToolbar, setShowOriginalToolbar] = useState(false);
  const [showOnlySelectedSection, setShowOnlySelectedSection] = useState(false);
  const [enableFloatingNavMenu, setEnableFloatingNavMenu] = useState(true);
  const [enableFloatingToolbarMenu, setEnableFloatingToolbarMenu] = useState(true);
  const [useSmallNavMenu, setUseSmallNavMenu] = useState(true);
  // Docked top-bar nav+toolbar vs floating menus, driven by the user's
  // navMenuStyle preference (Home → Preferences). Top bar is the default.
  const [showTopBar, setShowTopBar] = useState(true);
  const [selectedSectionId, setSelectedSectionId] = useState('Configuration');
  const [expandedSections, setExpandedSections] = useState({ 'Configuration': true, 'SizingAndFrame': true });

  // ============================================
  // NAVIGATION & ROUTING
  // ============================================
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ============================================
  // QUOTE/ITEM STATE
  // ============================================
  const [quoteId, setQuoteId] = useState();
  const [quoteItemId, setQuoteItemId] = useState();
  const [fkCustomerId, setFkCustomerId] = useState();
  const [fkUserId, setFkUserId] = useState();
  const [viewItem, setViewItem] = useState();
  const [reference_no, setReferenceNo] = useState();
  const [newQuoteItemId, setNewQuoteItemId] = useState(null);
  const [lastDateSaved, setLastDateSaved] = useState("NA");
  const [isDisabled, setIsDisabled] = useState(false);

  // ============================================
  // DATA STATE
  // ============================================
  const [doorData, setDoorData] = useState({});
  const [initialDoorDataJSON, setInitialDoorDataJSON] = useState(null);
  const [isViewDetailsVisible, setIsViewDetailsVisible] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [displayPrices, setDisplayPrices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // The legacy screen controls keep their own internal state; bumping formKey
  // remounts them so they pick up values loaded from a saved item.
  const [formKey, setFormKey] = useState(0);

  // ============================================
  // PLEATED SCREEN STATE
  // ============================================
  const [profileValue, setProfileValue] = useState(getScreenProfile(config.SCREEN_PROFILE).value);
  const defaultScreenType = getScreenType(config.SCREEN_TYPE);
  const [screenTypeValue, setScreenTypeValue] = useState({ value: defaultScreenType.value, label: defaultScreenType.label });
  const [measurementTypeValue, setMeasurementTypeValue] = useState(SCREEN_SIZE);
  const [frameWidthValue, setFrameWidthValue] = useState(config.SCREEN_WIDTH);
  const [frameHeightValue, setFrameHeightValue] = useState(config.SCREEN_HEIGHT);
  // Metric mirrors of the inch sizes; editing either side updates the other.
  const [widthMmValue, setWidthMmValue] = useState(inchToMm(config.SCREEN_WIDTH));
  const [heightMmValue, setHeightMmValue] = useState(inchToMm(config.SCREEN_HEIGHT));
  const [magnetLatchValue, setMagnetLatchValue] = useState("No");
  const [orientationValue, setOrientationValue] = useState(getScreenOrientation().value);

  // Finish
  const [screenFrameColorValue, setScreenFrameColorValue] = useState("White");
  const [gentekColorValue, setGentekColorValue] = useState({ value: "", label: "" });

  // Additional Items
  const requiredKeys = ["CascadingCombo1", "CascadingCombo2", "CascadingCombo3", "CascadingCombo4", "CascadingCombo5"];
  const [CascadingComboValue, setCascadingComboValue] = useState([]);

  // Notes & Discounts
  const [locationTagValue, setLocationTagValue] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [quantityValue, setQuantityValue] = useState("1");
  const [discountValue, setDiscountValue] = useState(0);

  // ============================================
  // CRM STATE
  // ============================================
  const [crm_product_id, set_crm_product_id] = useState();
  const [crmUserId, setCrmUserId] = useState(false);
  const [ctrl_discount_enabled, set_ctrl_discount_enabled] = useState(0);

  // ============================================
  // EMAIL DIALOG STATE
  // ============================================
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [showAltToolbar, setShowAltToolbar] = useState(false);
  const [zoom, setZoom] = useState(1.00);

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const init = async () => {
      const id = await get_crm_user_id();
      setCrmUserId(id);

      const ctrl_discount_status = await fetchUserField('ctrl_discount_enabled');
      set_ctrl_discount_enabled(ctrl_discount_status);

      const navMenuPref = await fetchUserField('enableFloatingNavMenu');
      setEnableFloatingNavMenu(navMenuPref ?? true);
      const toolbarMenuPref = await fetchUserField('enableFloatingToolbarMenu');
      setEnableFloatingToolbarMenu(toolbarMenuPref ?? true);
      const navStylePref = await fetchUserField('navMenuStyle');
      setShowTopBar(navStylePref !== 'floating');

      const crmProductId = searchParams.get("id") || "NA";
      let inputData = await authFetch(`/local/get-ww-input-data/${crmProductId}`);

      if (inputData?.quoteItemId) {
        setQuoteId(inputData.quoteId);
        setQuoteItemId(inputData.quoteItemId);
        setFkCustomerId(inputData.fkCustomerId);
        setFkUserId(inputData.fkUserId);
        setReferenceNo(inputData.reference_no);
      } else if (location.state) {
        const {
          quoteId, quoteItemId, fkCustomerId,
          fkUserId, viewItem, reference_no
        } = location.state;

        setQuoteId(quoteId);
        setQuoteItemId(quoteItemId);
        setFkCustomerId(fkCustomerId);
        setViewItem(viewItem);
        setFkUserId(fkUserId);
        setReferenceNo(reference_no);
      }

      // Opened from "Add ... from My Library": no quoteItemId, Save creates a new ItemQuote.
      if (location.state?.libraryDoorData) {
        const tplDoorData = location.state.libraryDoorData;
        setDoorData(tplDoorData);
        setFormData(tplDoorData);
        setIsViewDetailsVisible(true);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const loadEmail = async () => {
      const email = await getCustomerEmail(fkCustomerId);
      setCustomerEmail(email || "no customer email found");
    };

    if (fkCustomerId) {
      loadEmail();
    }
  }, [fkCustomerId]);

  // ============================================
  // ACTION HANDLERS
  // ============================================
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.05, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.05, 0.2));
  };

  const handleSendEmail = async () => {
    setShowEmailDialog(true);
  };

  const handlePdfDownload = async () => {
    setIsLoading(true);
    try {
      // toPdfAuto picks server-side vs legacy based on USE_PDF_SERVER (constants.js).
      await toPdfAuto({
        container: ".view-details",
        filename: `${doorData?.frameData?.name || "PleatedScreen"}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // SAVE QUOTE HANDLER
  // ============================================
  const handleSaveQuote = async (bSaveAndBack = false) => {
    try {
      if (bSaveAndBack) {
        const currentDoorDataJSON = JSON.stringify(doorData);
        const hasDataChanged = initialDoorDataJSON && (currentDoorDataJSON !== initialDoorDataJSON);

        if (hasDataChanged || !Number.isInteger(Number(quoteItemId))) {
          const userChoice = await confirmC("Do you want to save before exiting?");
          if (!userChoice) {
            navigate('/quotes', {
              state: { quoteId, quoteItemId }
            });
            return;
          }
        } else {
          navigate('/quotes', {
            state: { quoteId, quoteItemId }
          });
          return;
        }
      }

      const userId = getCookie('user_id');

      if (parseInt(userId) !== parseInt(fkUserId)) {
        await alertC("This quote item doesn't belong to you, you can't modify and save it.");
        if (bSaveAndBack) {
          navigate('/quotes', {
            state: { quoteId, quoteItemId }
          });
        }
        return;
      }

      const date = new Date();
      const formattedDate = date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      });
      setLastDateSaved(formattedDate);
      doorData.document.orderDate = formattedDate;

      if (!quoteId && !quoteItemId) {
        await alertC('Create new Quote and NEW item');
      }

      if (quoteId && !quoteItemId && !newQuoteItemId) {
        const payload = {
          fkQuoteId: quoteId,
          doorData: doorData,
          itemType: ITEM_PATH,
        };
        const itemRow = await authFetchRequest(
          `/itemquotes/`,
          payload,
          "POST",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );
        setNewQuoteItemId(itemRow?.id);

        if (crmDefined && (crmUserId !== "NA")) {
          const product_id = await createCrmProduct({
            quoteId, itemRow, doorData,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
          if (product_id) set_crm_product_id(product_id);
        }
      }

      if ((quoteId && quoteItemId) || (quoteId && newQuoteItemId)) {
        const payload = {
          fkQuoteId: quoteId,
          doorData: doorData
        };
        const currentQuoteItemId = quoteItemId || newQuoteItemId;
        const itemRow = await authFetchRequest(
          `/itemquotes/${currentQuoteItemId}`,
          payload,
          "PUT",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );

        if (crmDefined && (crmUserId !== "NA")) {
          await updateCrmProduct({
            quoteId, itemRow, doorData, crm_product_id,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
        }
      }

      if (bSaveAndBack) {
        navigate('/quotes', {
          state: { quoteId, quoteItemId }
        });
      }
      return;

    } catch (err) {
      console.error("Failed to save quote and item quote:", err);
      alert("Failed to save.");
    }
  };

  const toggleViewDetails = () => {
    setIsViewDetailsVisible(prev => !prev);
  };

  const togglePrices = () => {
    setDisplayPrices(!displayPrices);
  };

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    if (viewItem === 'readonly') {
      setIsDisabled(true);
      setDisplayPrices(true);
    }

    const fetchData = async () => {
      try {
        if (!quoteId || !quoteItemId) {
          console.warn("Missing quoteId or quoteItemId—skipping fetch.");
          return;
        }

        const data = await authFetch(`/itemquotes/${quoteItemId}`);

        if (!data?.doorData) {
          console.warn("No valid doorData found.");
          alert("No valid doorData found.");
          return;
        }

        setDoorData(data.doorData);

        if (crmDefined) {
          set_crm_product_id(data?.crm_product_id || null);
        }
        setFormData(data.doorData);
        setIsViewDetailsVisible(true);
        setHasLoaded(true);

      } catch (error) {
        console.error("Fetch error:", error);
        alert(`Error: ${error.message}`);
      }
    };

    fetchData();
  }, [quoteId, quoteItemId]);

  // ============================================
  // SET FORM DATA FROM LOADED DATA
  // Also accepts items saved by the original pleatedscreen.js (no screenType key).
  // ============================================
  const setFormData = (doorData) => {
    const fd = doorData?.frameData || {};
    setQuantityValue(fd.quantity || "1");
    setDiscountValue(doorData?.discounts?.discount || "0");

    setProfileValue(getScreenProfile(fd.profile).value);
    const legacyType = fd.selectedDoor?.id === 2 ? 'Double' : 'Single';
    const type = getScreenType(fd.screenType?.value || legacyType);
    setScreenTypeValue({ value: type.value, label: type.label });

    setMeasurementTypeValue(fd.measurementType || SCREEN_SIZE);
    const widthLabel = fd.frameWidthLabel || (fd.frameWidth ? formatToFractional(fd.frameWidth) : config.SCREEN_WIDTH);
    const heightLabel = fd.frameHeightLabel || (fd.frameHeight ? formatToFractional(fd.frameHeight) : config.SCREEN_HEIGHT);
    setFrameWidthValue(widthLabel);
    setFrameHeightValue(heightLabel);
    setWidthMmValue(fd.frameWidth_mm ? String(fd.frameWidth_mm) : inchToMm(widthLabel));
    setHeightMmValue(fd.frameHeight_mm ? String(fd.frameHeight_mm) : inchToMm(heightLabel));
    setMagnetLatchValue(fd.magnetLatch || "No");
    setOrientationValue(getScreenOrientation(fd.orientation).value);

    setScreenFrameColorValue(fd.screenFrameColor || "White");
    setGentekColorValue({ value: fd.gentekColor || "", label: fd.gentekColor_label || "" });

    setLocationTagValue(doorData?.notes?.LocationTag || doorData?.notes?.locationTag || "");
    setNotesValue(doorData?.notes?.notes || "");

    if (doorData?.additionalItems !== undefined) {
      const convertedCombos = Object.entries(doorData.additionalItems)
            .filter(([_, val]) => val !== "NA")
            .map(([id, value]) => ({ id, value }));
      setCascadingComboValue(convertedCombos);
    }
    if (doorData?.document?.orderDate) {
      setLastDateSaved(doorData.document.orderDate);
    }
    setFormKey(prev => prev + 1);
  };

  // ============================================
  // PROFILE RESTRICTIONS
  // Z-19-45: single leaf only, measured as screen size only.
  // ============================================
  const profile = getScreenProfile(profileValue);

  const handleProfileChange = (newValue) => {
    setProfileValue(newValue);
    const newProfile = getScreenProfile(newValue);
    if (newProfile.singleLeafOnly && screenTypeValue?.value !== 'Single') {
      const single = getScreenType('Single');
      setScreenTypeValue({ value: single.value, label: single.label });
    }
    if (newProfile.screenSizeOnly && measurementTypeValue !== SCREEN_SIZE) {
      setMeasurementTypeValue(SCREEN_SIZE);
    }
  };

  // Also enforced here so items saved with an inconsistent combination are corrected.
  const effectiveScreenTypeValue = profile.singleLeafOnly ? 'Single' : screenTypeValue?.value;
  const effectiveMeasurementType = profile.screenSizeOnly ? SCREEN_SIZE : measurementTypeValue;

  // ============================================
  // SIZE LIMITS (per screen type)
  // ============================================
  const screenType = getScreenType(effectiveScreenTypeValue);
  const { minW, maxW, minH, maxH } = screenType;
  const isSingleLeaf = screenType.value === 'Single';

  // ============================================
  // INCH <-> MM SIZE LINKING
  // ============================================
  const handleWidthInchChange = (newValue) => {
    setFrameWidthValue(newValue);
    setWidthMmValue(inchToMm(newValue));
  };
  const handleHeightInchChange = (newValue) => {
    setFrameHeightValue(newValue);
    setHeightMmValue(inchToMm(newValue));
  };
  const handleWidthMmChange = (newValue) => {
    setWidthMmValue(newValue);
    const inches = mmToInch(newValue);
    if (inches) setFrameWidthValue(inches);
  };
  const handleHeightMmChange = (newValue) => {
    setHeightMmValue(newValue);
    const inches = mmToInch(newValue);
    if (inches) setFrameHeightValue(inches);
  };

  // ============================================
  // BUILD CONTROL MAP
  // ============================================
  const controlMap = [
    // ---- Configuration ----
    {
      id: "ScreenProfile",
      component: (
        <ScreenProfile
          disabled={isDisabled}
          value={profileValue}
          onChange={handleProfileChange}
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Configuration"
    },
    {
      id: "ScreenType",
      component: (
        <ScreenType
          disabled={isDisabled}
          value={{ value: screenType.value, label: screenType.label }}
          allowedTypes={profile.singleLeafOnly ? ['Single'] : null}
          onChange={(newValue) => setScreenTypeValue(newValue)}
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "Configuration"
    },

    // ---- Sizing & Options ----
    {
      id: "MeasurmentType",
      component: (
        <MeasurmentType
          key={`MeasurmentType-${formKey}`}
          disabled={isDisabled}
          label="Measurement Type"
          value={effectiveMeasurementType}
          screenSizeOnly={profile.screenSizeOnly}
          onChange={(newValue) => setMeasurementTypeValue(newValue)}
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "FrameWidth",
      component: (
        <FrameWidthEticomN
          key={`FrameWidth-${formKey}`}
          disabled={isDisabled}
          label="Screen Width"
          value={frameWidthValue}
          minValue={minW}
          maxValue={maxW}
          onChange={handleWidthInchChange}
        />
      ),
      position: [2, 1],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "FrameWidthMm",
      component: (
        <SizeMm
          id="FrameWidthMm"
          disabled={isDisabled}
          label="Screen Width (mm)"
          value={widthMmValue}
          minValue={minW}
          maxValue={maxW}
          onChange={handleWidthMmChange}
        />
      ),
      position: [3, 1],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "FrameHeightMm",
      component: (
        <SizeMm
          id="FrameHeightMm"
          disabled={isDisabled}
          label="Screen Height (mm)"
          value={heightMmValue}
          minValue={minH}
          maxValue={maxH}
          onChange={handleHeightMmChange}
        />
      ),
      position: [3, 2],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "FrameHeight",
      component: (
        <FrameHeightEticomN
          key={`FrameHeight-${formKey}`}
          disabled={isDisabled}
          label="Screen Height"
          value={frameHeightValue}
          minValue={minH}
          maxValue={maxH}
          onChange={handleHeightInchChange}
        />
      ),
      position: [2, 2],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "MagnetLatch",
      component: (
        <MagnetLatch
          key={`MagnetLatch-${formKey}`}
          disabled={isDisabled}
          label="Magnet Latch"
          value={magnetLatchValue}
          onChange={(newValue) => setMagnetLatchValue(newValue)}
        />
      ),
      position: [4, 1],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    // Orientation only applies to a single leaf (a double leaf closes in the middle)
    ...(isSingleLeaf
      ? [
          {
            id: "Orientation",
            component: (
              <ScreenOrientation
                disabled={isDisabled}
                value={orientationValue}
                onChange={(newValue) => setOrientationValue(newValue)}
              />
            ),
            position: [4, 2],
            status: "active",
            expandableSectionId: "SizingAndFrame"
          },
        ]
      : []),

    // ---- Finish ----
    {
      id: "ScreenFrameColor",
      component: (
        <ExteriorDoor
          key={`ScreenFrameColor-${formKey}`}
          disabled={isDisabled}
          label="Screen Frame Color"
          value={screenFrameColorValue}
          onChange={(newValue) => setScreenFrameColorValue(newValue)}
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Finish"
    },
    {
      id: "GentekColor",
      component: (
        <GentekColor
          controlId={1}
          disabled={isDisabled}
          label="Gentek Color"
          value={gentekColorValue}
          color={screenFrameColorValue}
          onChange={(newValue) => setGentekColorValue(newValue)}
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "Finish"
    },

    // ---- Additional Items ----
    {
      id: "CascadingCombo", component: <CascadingCombo
        disabled={isDisabled} label='item' value={CascadingComboValue}
        onChange={(newValue) => { setCascadingComboValue(newValue); }}
      />,
      position: [1, 1], status: "active", expandableSectionId: "AdditionalItems"
    },

    // ---- Notes ----
    { id: "LocationTag", component: <LocationTag
      disabled={isDisabled}
      value={locationTagValue}
      onChange={(newValue) => { setLocationTagValue(newValue); }}
    />, position: [1, 1], status: "active", expandableSectionId: "Notes" },

    { id: "Notes", component: <Notes
      disabled={isDisabled}
      value={notesValue}
      onChange={(newValue) => { setNotesValue(newValue); }}
    />, position: [2, "1 / span 3"], status: "active", expandableSectionId: "Notes" },

    // ---- Quantity & Discounts ----
    ...(ctrl_discount_enabled === 1
      ? [
          {
            id: "Discount",
            component: (
              <Discount
                disabled={isDisabled}
                label="Discount %"
                value={discountValue}
                onChange={(newValue) => setDiscountValue(newValue)}
              />
            ),
            position: [1, 1],
            status: "active",
            expandableSectionId: "Discounts",
          },
        ]
      : []),

    { id: "Quantity", component: (
      <Quantity
        disabled={isDisabled}
        value={quantityValue}
        onChange={(newValue) => { setQuantityValue(newValue); }}
      />
    ), position: [1, 1], status: "active", expandableSectionId: "Discounts" },
  ];

  // ============================================
  // DOOR DATA BUILDING
  // ============================================
  doorData.calc = doorData.calc || {};
  doorData.frameData = doorData.frameData || {};
  doorData.notes = doorData.notes || {};
  doorData.additionalItems = doorData.additionalItems || {};
  doorData.discounts = doorData.discounts || {};
  doorData.document = doorData.document || {};

  doorData.document.type = DOCUMENT_TYPE;
  doorData.document.orderNumber = `${quoteId}/${quoteItemId || newQuoteItemId || 'new item'}`;
  doorData.document.quoteId = quoteId;
  doorData.document.quoteItemId = quoteItemId;
  doorData.document.reference = `${reference_no}`;
  doorData.document.orderDate = `${lastDateSaved}`;

  doorData.quoteTemplate = QUOTE_TEMPLATE;
  doorData.priceRules = PRICE_RULES;
  doorData.displayPrices = displayPrices;
  doorData.quote = 1;

  // selectedDoor {id, name} is what pleated_screen_template / priceRules and ViewDetails read
  doorData.frameData.profile = profile.value;
  doorData.frameData.screenType = { value: screenType.value, label: screenType.label };
  doorData.frameData.selectedDoor = { id: screenType.id, name: screenType.label };
  doorData.frameData.name = `Pleated Screen - ${screenType.value === 'Double' ? 'Double' : 'Single'} Leaf`;
  doorData.frameData.quantity = quantityValue || 1;

  // Out-of-range sizes are clamped for drawing/pricing; the control shows the error.
  const clampSize = (value, min, max) => {
    const d = formatToDecimal(value);
    const isValid = d >= min && d <= max;
    const size = isValid ? d : (d < min ? min : max);
    return { isValid, size, label: isValid ? value : `${size}"` };
  };
  const widthSize = clampSize(frameWidthValue, minW, maxW);
  const heightSize = clampSize(frameHeightValue, minH, maxH);

  doorData.frameData.isValidW = widthSize.isValid;
  doorData.frameData.isValidH = heightSize.isValid;
  doorData.frameData.frameWidth = widthSize.size;
  doorData.frameData.frameWidthLabel = widthSize.label;
  doorData.frameData.frameWidthRound = Math.round(widthSize.size);
  doorData.frameData.frameHeight = heightSize.size;
  doorData.frameData.frameHeightLabel = heightSize.label;
  doorData.frameData.frameHeightRound = Math.round(heightSize.size);
  // Same keys windows use, read by the Zoho CRM product sync
  doorData.frameData.width_d = widthSize.size;
  doorData.frameData.height_d = heightSize.size;
  doorData.frameData.frameWidth_mm = parseInt(widthMmValue) || null;
  doorData.frameData.frameHeight_mm = parseInt(heightMmValue) || null;
  doorData.frameData.totalSquare = ((widthSize.size * heightSize.size) / 144).toFixed(2);

  doorData.frameData.measurementType = effectiveMeasurementType;
  doorData.frameData.magnetLatch = magnetLatchValue;
  // Kept in state while a double leaf is selected, but only saved when it applies.
  doorData.frameData.orientation = isSingleLeaf ? orientationValue : null;

  doorData.frameData.screenFrameColor = screenFrameColorValue;
  doorData.frameData.gentekColor = gentekColorValue?.value;
  doorData.frameData.gentekColor_label = gentekColorValue?.label;

  doorData.notes.LocationTag = locationTagValue;
  doorData.notes.notes = notesValue;

  doorData.additionalItems = {};
  CascadingComboValue.forEach(({ id, value }) => {
    doorData.additionalItems[id] = value;
  });
  requiredKeys.forEach(key => {
    if (!doorData.additionalItems.hasOwnProperty(key)) {
      doorData.additionalItems[key] = "NA";
    }
  });

  doorData.discounts.discount = discountValue;

  // ============================================
  // CHANGE DETECTION
  // ============================================
  if (hasLoaded && initialDoorDataJSON === null && doorData && Object.keys(doorData).length > 0) {
    setInitialDoorDataJSON(JSON.stringify(doorData));
  }

  const doorDataCopy = { ...doorData };
  const doorDataCopy2 = { ...doorData };

  // ============================================
  // SECTION NAVIGATION
  // ============================================
  const scrollToSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    setTimeout(() => {
      const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const handleSectionExpandChange = (sectionId, isExpanded) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: isExpanded }));
  };

  const isSectionVisible = (sectionId) => {
    if (!showOnlySelectedSection) return true;
    return selectedSectionId === sectionId;
  };

  const getForceExpanded = (sectionId) => {
    if (showOnlySelectedSection && selectedSectionId === sectionId) {
      return true;
    }
    return null;
  };

  // ============================================
  // CALC DATA CALLBACK
  // ============================================
  const updateCalcData = (newCalc) => {
    setDoorData(prev => ({
      ...prev,
      calc: {
        ...prev.calc,
        ...newCalc,
      },
    }));
  };

  // ============================================
  // SECTION DEFINITIONS FOR PLEATED SCREENS
  // Ids are shared with the window/patio door nav menus; the ones a screen
  // doesn't have are listed in hiddenSections.
  // ============================================
  const sections = [
    { id: 'Configuration', title: '🛠️ Configuration' },
    { id: 'SizingAndFrame', title: '📐 Sizing & Options' },
    { id: 'Finish', title: '🎨 Finish' },
    { id: 'AdditionalItems', title: '➕ Additional Items' },
    { id: 'Notes', title: '📝 Notes' },
    { id: 'Discounts', title: '🏷️ Quantity' },
  ];
  const topBarMenuItems = [
    { id: 'Configuration', label: 'Configuration', tooltip: 'Screen Configuration' },
    { id: 'SizingAndFrame', label: 'Sizing & Options', tooltip: 'Sizing & Options' },
    { id: 'Finish', label: 'Finish', tooltip: 'Finish' },
    { id: 'AdditionalItems', label: 'Additional Items', tooltip: 'Additional Items' },
    { id: 'Notes', label: 'Notes', tooltip: 'Notes' },
    { id: 'Discounts', label: 'Quantity & Discounts', tooltip: 'Quantity & Discounts' },
  ];

  const disabledSections = {};
  const hiddenSections = ['SizingAndFrameTop', 'GrillsTop', 'Grills', 'Glass', 'Installation'];

  const drawingToLeft = isViewDetailsVisible ? 40 : 0;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className='entire-container' style={showTopBar ? { marginTop: '-1.5rem' } : undefined}>
      {/* Floating Nav Menu */}
      {enableFloatingNavMenu && !showTopBar && useSmallNavMenu && (
        <SmallFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(false)}
        />
      )}
      {enableFloatingNavMenu && !showTopBar && !useSmallNavMenu && (
        <LargeFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(true)}
        />
      )}

      {/* Floating Toolbar Menu */}
      {enableFloatingToolbarMenu && !showTopBar && (
        <SmallFloatingToolbarMenu
          isViewDetailsVisible={isViewDetailsVisible}
          toggleViewDetails={toggleViewDetails}
          displayPrices={displayPrices}
          togglePrices={togglePrices}
          isLoading={isLoading}
          handlePdfDownload={handlePdfDownload}
          handleSendEmail={handleSendEmail}
          handleSaveQuote={handleSaveQuote}
          hideFlip={true}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          showOriginalToolbar={showOriginalToolbar}
          toggleOriginalToolbar={() => setShowOriginalToolbar(prev => !prev)}
        />
      )}

      <div className={`door-container-left ${showTopBar ? '' : 'shift-down'}`}>
        {/* Original Toolbar */}
        {(showOriginalToolbar || !enableFloatingToolbarMenu) && (
          <div className="toolbar-container">
            <div className="toolbar">
              <button
                className="toolbar-button"
                onClick={toggleViewDetails}
              >
                {isViewDetailsVisible ? "Hide Details" : "View Details"}
              </button>
              <button className="toolbar-button" onClick={togglePrices} disabled={!isViewDetailsVisible}>
                {displayPrices ? "Hide Prices" : "Show Prices"}
              </button>
              <button
                className={`toolbar-button ${isLoading ? "loading" : ""}`}
                onClick={handlePdfDownload}
                disabled={!isViewDetailsVisible || isLoading}
              >
                {isLoading ? <span className="spinner"></span> : "PDF"}
              </button>
              <button className="toolbar-button" onClick={() => handleSendEmail(true)} disabled={!isViewDetailsVisible}>
                Email
              </button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(false)}>
                Save
              </button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(true)}>
                Exit
              </button>

              <span
                className="arrow"
                onClick={() => setShowAltToolbar(prev => !prev)}
                title="Toggle more tools"
              >
                {showAltToolbar ? "▲" : "▼"}
              </span>
            </div>
            {showAltToolbar && (
              <div className="toolbar toolbar-secondary">
                <button className="toolbar-button" onClick={handleZoomIn}>
                  Zoom In
                </button>
                <button className="toolbar-button" onClick={handleZoomOut}>
                  Zoom Out
                </button>
              </div>
            )}
          </div>
        )}

        <div>
          {showTopBar && (enableFloatingToolbarMenu || enableFloatingNavMenu) ? (
            // Caption lives inside the sticky header so the docked bars never
            // cover it on scroll; the whole block stays pinned at the top.
            <div style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: tc.panelBg, paddingBottom: '6px' }}>
              <div className="door">
                <p><b><i>{doorData.frameData.name || "Pleated Screen"}</i></b></p>
              </div>
              {enableFloatingToolbarMenu && (
                <TopBarToolbarMenu
                  isViewDetailsVisible={isViewDetailsVisible}
                  toggleViewDetails={toggleViewDetails}
                  displayPrices={displayPrices}
                  togglePrices={togglePrices}
                  isLoading={isLoading}
                  handlePdfDownload={handlePdfDownload}
                  handleSendEmail={handleSendEmail}
                  handleSaveQuote={handleSaveQuote}
                  hideFlip={true}
                  handleZoomIn={handleZoomIn}
                  handleZoomOut={handleZoomOut}
                />
              )}
              {enableFloatingNavMenu && (
                <TopBarNavMenu
                  menuItems={topBarMenuItems}
                  selectedSectionId={selectedSectionId}
                  showOnlySelected={showOnlySelectedSection}
                  disabledSections={disabledSections}
                  hiddenSections={hiddenSections}
                  onSelectSection={(id) => {
                    setShowOnlySelectedSection(true);
                    setSelectedSectionId(id);
                    setExpandedSections(prev => ({ ...prev, [id]: true }));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  onViewAll={() => {
                    setShowOnlySelectedSection(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              )}
            </div>
          ) : (
            <div>
              <div className="door">
                <p><b><i>{doorData.frameData.name || "Pleated Screen"}</i></b></p>
              </div>
            </div>
          )}

          {/* Render expandable sections */}
          {sections.map(section => {
            const sectionControls = controlMap.filter(c => c.expandableSectionId === section.id);

            return isSectionVisible(section.id) && (
              <ExpandableSection
                key={section.id}
                title={section.title}
                panelLocation="2"
                controlMap={sectionControls}
                sectionId={section.id}
                state={expandedSections[section.id] || false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded(section.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Drawing Area */}
      <div
        className={
          `door-container ${showTopBar ? '' : 'shift-down'} image-container1 ` +
          (isViewDetailsVisible ? "hidden-left-panel" : "")
        }
      >
        <div className="fixed-container_new">
          <DrawWindow
            doorData={doorDataCopy2}
            toLeft={drawingToLeft}
            zoom={zoom}
          />
        </div>
      </div>

      {/* View Details Sliding Panel */}
      <ViewDetails
        isVisible={isViewDetailsVisible}
        data={viewDetailsData}
        doorData={doorDataCopy}
        updateCalcData={updateCalcData}
        zoom={zoom}
        drawing={
          <DrawWindow
            doorData={doorDataCopy2}
            toLeft={drawingToLeft}
            zoom={zoom}
          />
        }
      />

      {/* Email Dialog */}
      {showEmailDialog && (
        <EmailDialog
          isOpen={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          isSending={isSending}
          setIsSending={setIsSending}
          customerEmail={customerEmail}
        />
      )}
    </div>
  );
};

export default PleatedScreenN;
